/**
 * Framework-free DOM pieces for the widget loader (MER-702): feature
 * detection, the first-paint skeleton, and the link-out fallback. Keep this
 * file free of React / app imports — it ships in the small widget.js loader.
 */

export const LIGHT_FALLBACK_CLASS = 'meo-widget-fallback';
export const LIGHT_FALLBACK_MANAGE_CLASS = 'meo-widget-fallback-manage';
export const LIGHT_LOADING_CLASS = 'meo-widget-loading';

const LOADER_STYLE_ATTR = 'data-meo-loader-styles';
const SKELETON_ATTR = 'data-meo-skeleton';
const SHADOW_FALLBACK_ATTR = 'data-meo-fallback';
const LIGHT_GENERATED_FALLBACK_ATTR = 'data-meo-generated-fallback';

const TRACKING_PARAM_PATTERN = /^(utm_[a-z0-9_]+|source|campaign)$/i;

// Height reserved before the card mounts (CLS). Hosts can tune it with
// --meo-embed-placeholder-height; the snippet placeholder uses the same var.
export const PLACEHOLDER_MIN_HEIGHT = 'var(--meo-embed-placeholder-height, 320px)';

// Card surface shared by the skeleton and the link-out fallback. Same Canvas
// default as the live widget card, with its own text color so the links stay
// readable when the host text color matches the page background.
const CARD_STYLE = 'box-sizing:border-box;padding:1.25em;border:1px solid var(--meo-embed-card-border,rgba(127,127,127,.25));border-radius:12px;background:var(--meo-embed-card-bg,Canvas);color:CanvasText;font:inherit';
const CTA_STYLE = 'display:block;box-sizing:border-box;width:100%;max-width:20em;margin:0 auto;padding:.625em 1em;border:1px solid var(--meo-embed-accent,CanvasText);border-radius:6px;background:var(--meo-embed-accent,CanvasText);color:Canvas;font:inherit;font-weight:600;text-decoration:none';
const MANAGE_STYLE = 'color:inherit;font:inherit;font-size:.875em';

const LOADER_CSS = [
  `.meo-loader,.meo-loader-fallback{${CARD_STYLE};min-height:${PLACEHOLDER_MIN_HEIGHT}}`,
  '.meo-loader__bar{display:block;height:.9em;margin:0 0 .75em;border-radius:6px;background:currentColor;opacity:.08;animation:meo-loader-pulse 1.4s ease-in-out infinite}',
  '.meo-loader__bar--title{width:60%;height:1.4em}',
  '.meo-loader__bar--short{width:40%}',
  '.meo-loader__bar--row{height:3em;margin-top:1.25em}',
  '.meo-loader__bar--cta{height:2.75em;margin:1.25em 0 0}',
  '.meo-loader__sr{position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}',
  '.meo-loader-fallback{display:flex;flex-direction:column;justify-content:center;gap:.75em;text-align:center}',
  '.meo-loader-fallback p{margin:0}',
  `.meo-loader-fallback__cta{${CTA_STYLE}}`,
  `.meo-loader-fallback__manage{${MANAGE_STYLE}}`,
  '@keyframes meo-loader-pulse{50%{opacity:.16}}',
  '@media (prefers-reduced-motion:reduce){.meo-loader__bar{animation:none}}',
].join('');

export const FALLBACK_MESSAGE = 'Registration couldn’t load here.';
export const FALLBACK_REGISTER_TEXT = 'Get tickets';
export const FALLBACK_MANAGE_TEXT = 'Already registered? Manage registration';

/**
 * True when this browser can run the widget app: Shadow DOM, module scripts
 * (the app bundle is an ES module), fetch, and the ES2015+ globals it uses.
 */
export function supportsWidgetRuntime(win) {
  try {
    return Boolean(
      win
      && typeof win.Promise === 'function'
      && typeof win.fetch === 'function'
      && typeof win.Map === 'function'
      && typeof win.URL === 'function'
      && typeof win.CustomEvent === 'function'
      && win.Element
      && typeof win.Element.prototype.attachShadow === 'function'
      && win.HTMLScriptElement
      && 'noModule' in win.HTMLScriptElement.prototype,
    );
  } catch {
    return false;
  }
}

/**
 * Hosted app origin for the link-out fallback, derived from the loader URL
 * (`https://embed.<zone>/widget.js` → `https://app.<zone>`).
 */
export function deriveAppOrigin(scriptSrc) {
  try {
    const url = new URL(scriptSrc);
    if (!url.hostname.startsWith('embed.')) {
      return null;
    }
    return `${url.protocol}//app.${url.host.slice('embed.'.length)}`;
  } catch {
    return null;
  }
}

/** Copy host-page attribution params (MER-578) onto a fallback link. */
export function appendTrackingParams(href, search) {
  if (!href || !search) {
    return href;
  }
  try {
    const url = new URL(href);
    const hostParams = new URLSearchParams(search);
    hostParams.forEach((value, key) => {
      if (TRACKING_PARAM_PATTERN.test(key) && value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  } catch {
    return href;
  }
}

function withSearchParam(href, key, value) {
  if (!value) {
    return href;
  }
  return `${href}${href.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(value)}`;
}

/**
 * Register / manage URLs for the link-out fallback. Prefers the hrefs from
 * the snippet's own fallback anchors (MER-712) when present.
 */
export function buildFallbackLinks({ container, config, appOrigin, search }) {
  const snippetRegister = container?.querySelector?.(`a.${LIGHT_FALLBACK_CLASS}`)?.href || '';
  const snippetManage = container?.querySelector?.(`a.${LIGHT_FALLBACK_MANAGE_CLASS}`)?.href || '';
  const hostedBase = appOrigin && config?.eventId
    ? `${appOrigin}/${encodeURIComponent(config.eventId)}/register`
    : '';

  const register = snippetRegister || (hostedBase && withSearchParam(hostedBase, 'coupon', config.couponCode));
  const manage = snippetManage || (hostedBase && withSearchParam(hostedBase, 'view', 'manage'));

  return {
    register: appendTrackingParams(register, search) || '',
    manage: appendTrackingParams(manage, search) || '',
  };
}

function ensureLoaderStyles(shadowRoot) {
  if (shadowRoot.querySelector(`style[${LOADER_STYLE_ATTR}]`)) {
    return;
  }
  const style = shadowRoot.ownerDocument.createElement('style');
  style.setAttribute(LOADER_STYLE_ATTR, 'true');
  style.textContent = LOADER_CSS;
  shadowRoot.insertBefore(style, shadowRoot.firstChild);
}

function createElement(doc, tag, className, text) {
  const el = doc.createElement(tag);
  if (className) {
    el.className = className;
  }
  if (text) {
    el.textContent = text;
  }
  return el;
}

/** Skeleton card shown in the Shadow Root while the app bundle loads. */
export function renderSkeleton(shadowRoot) {
  if (shadowRoot.querySelector(`[${SKELETON_ATTR}]`)) {
    return;
  }
  ensureLoaderStyles(shadowRoot);
  const doc = shadowRoot.ownerDocument;
  const skeleton = createElement(doc, 'div', 'meo-loader');
  skeleton.setAttribute(SKELETON_ATTR, 'true');
  skeleton.setAttribute('role', 'status');
  skeleton.setAttribute('aria-busy', 'true');
  skeleton.appendChild(createElement(doc, 'span', 'meo-loader__sr', 'Loading registration…'));
  [
    'meo-loader__bar meo-loader__bar--title',
    'meo-loader__bar meo-loader__bar--short',
    'meo-loader__bar',
    'meo-loader__bar meo-loader__bar--row',
    'meo-loader__bar meo-loader__bar--row',
    'meo-loader__bar meo-loader__bar--cta',
  ].forEach((className) => {
    const bar = createElement(doc, 'span', className);
    bar.setAttribute('aria-hidden', 'true');
    skeleton.appendChild(bar);
  });
  shadowRoot.appendChild(skeleton);
}

function buildFallbackNode(doc, links, { inlineStyles = false } = {}) {
  const wrapper = createElement(doc, 'div', 'meo-loader-fallback');
  wrapper.setAttribute('role', 'note');
  if (inlineStyles) {
    wrapper.setAttribute('style', `${CARD_STYLE};text-align:center`);
  }
  wrapper.appendChild(createElement(doc, 'p', '', FALLBACK_MESSAGE));
  if (links.register) {
    const cta = createElement(doc, 'a', 'meo-loader-fallback__cta', FALLBACK_REGISTER_TEXT);
    cta.setAttribute('href', links.register);
    if (inlineStyles) {
      cta.setAttribute('style', `${CTA_STYLE};margin:.75em auto`);
    }
    wrapper.appendChild(cta);
  }
  if (links.manage) {
    const manage = createElement(doc, 'a', 'meo-loader-fallback__manage', FALLBACK_MANAGE_TEXT);
    manage.setAttribute('href', links.manage);
    if (inlineStyles) {
      manage.setAttribute('style', MANAGE_STYLE);
    }
    wrapper.appendChild(manage);
  }
  return wrapper;
}

/** Replace the skeleton with the link-out fallback (script/boot failure). */
export function renderShadowFallback(shadowRoot, links) {
  removeSkeleton(shadowRoot);
  if (shadowRoot.querySelector(`[${SHADOW_FALLBACK_ATTR}]`)) {
    return;
  }
  ensureLoaderStyles(shadowRoot);
  const node = buildFallbackNode(shadowRoot.ownerDocument, links);
  node.setAttribute(SHADOW_FALLBACK_ATTR, 'true');
  const reactRoot = shadowRoot.querySelector('[data-meo-react-root]');
  shadowRoot.insertBefore(node, reactRoot || null);
}

export function removeSkeleton(shadowRoot) {
  shadowRoot?.querySelector?.(`[${SKELETON_ATTR}]`)?.remove();
}

export function removeShadowFallback(shadowRoot) {
  shadowRoot?.querySelector?.(`[${SHADOW_FALLBACK_ATTR}]`)?.remove();
}

/**
 * Unsupported browser (no Shadow DOM / module scripts): show the link-out in
 * the light DOM. Reuses the snippet's own fallback links when present.
 */
export function renderLightFallback(container, links) {
  const doc = container.ownerDocument;
  const snippetLinks = container.querySelectorAll(`.${LIGHT_FALLBACK_CLASS}, .${LIGHT_FALLBACK_MANAGE_CLASS}`);
  if (snippetLinks.length > 0) {
    snippetLinks.forEach((link) => {
      link.style.animation = 'none';
      const href = link.classList.contains(LIGHT_FALLBACK_MANAGE_CLASS) ? links.manage : links.register;
      if (href) {
        link.setAttribute('href', href);
      }
    });
    container.querySelectorAll(`.${LIGHT_LOADING_CLASS}`).forEach((el) => {
      // The snippet <style> shows this with display:block!important.
      el.style.setProperty('display', 'none', 'important');
    });
    return;
  }
  if (container.querySelector(`[${LIGHT_GENERATED_FALLBACK_ATTR}]`)) {
    return;
  }
  const node = buildFallbackNode(doc, links, { inlineStyles: true });
  node.setAttribute(LIGHT_GENERATED_FALLBACK_ATTR, 'true');
  container.appendChild(node);
}

export function removeLightFallback(container) {
  container?.querySelector?.(`[${LIGHT_GENERATED_FALLBACK_ATTR}]`)?.remove();
}
